import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper,
  Card,
  CardContent,
  Button,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton
} from '@mui/material';
import { getListKarmaByDao, getListCreditByDao, getHistoryWalletByDao, getKarmaCreditActivityByEmail } from '../../../../../redux/actions/daoUI/engagement.action';
import DetailModal from './components/DetailModal';
const UNIT_TYPE = {
  KAT: 'KAT',
  KYTE: 'KYTE',
  CREDITS: 'CREDITS',
  KARMAS: 'KARMAS',
};
const { KAT, KYTE, CREDITS, KARMAS } = UNIT_TYPE;

// TabPanel component for tab content
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`engagement-tabpanel-${index}`}
      aria-labelledby={`engagement-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const EngagementTab = ({ chainId, currentDao, daoOrganizerAddress }) => {
  const [isOrganizer, setIsOrganizer] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  
  // Karma data state
  const [karmaData, setKarmaData] = useState([]);
  const [karmaLoading, setKarmaLoading] = useState(false);
  const [karmaError, setKarmaError] = useState(null);
  const [karmaPage, setKarmaPage] = useState(1);
  const [karmaTotal, setKarmaTotal] = useState(0);
  
  // Credit data state
  const [creditData, setCreditData] = useState([]);
  const [creditLoading, setCreditLoading] = useState(false);
  const [creditError, setCreditError] = useState(null);
  const [creditPage, setCreditPage] = useState(1);
  const [creditTotal, setCreditTotal] = useState(0);
  
  // Wallet history data state
  const [walletData, setWalletData] = useState([]);
  const [walletLoading, setWalletLoading] = useState(false);
  const [walletError, setWalletError] = useState(null);
  const [walletPage, setWalletPage] = useState(1);
  const [walletTotal, setWalletTotal] = useState(0);
  
  // Enhanced filter state
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    email: '',
    status: 'all',
    units: {
      KAT: true,
      KYTE: true,
      CREDITS: true,
      KARMAS: true
    }
  });
  
  // Add new filter states for Karma and Credit
  const [karmaFilters, setKarmaFilters] = useState({
    startDate: '',
    endDate: '',
    search: '',
    minAmount: '',
    maxAmount: ''
  });

  const [creditFilters, setCreditFilters] = useState({
    startDate: '',
    endDate: '',
    search: '',
    minAmount: '',
    maxAmount: ''
  });
  
  const limit = 10;

  // Add new state for modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [modalType, setModalType] = useState('karma'); // 'karma' or 'credit'
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    const checkOrganizerPermission = () => {
      if (window.ethereum && window.ethereum.selectedAddress && daoOrganizerAddress) {
        const currentAddress = window.ethereum.selectedAddress.toLowerCase();
        const organizerAddress = daoOrganizerAddress.toLowerCase();
        setIsOrganizer(currentAddress === organizerAddress);
      } else {
        setIsOrganizer(false);
      }
    };

    checkOrganizerPermission();

    // Listen for account changes
    if (window.ethereum) {
      const handleAccountsChanged = () => {
        checkOrganizerPermission();
      };

      window.ethereum.on('accountsChanged', handleAccountsChanged);

      return () => {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
      };
    }
  }, [daoOrganizerAddress]);

  // Filter handlers
  const handleKarmaFilterChange = (type, value) => {
    setKarmaFilters(prev => ({
      ...prev,
      [type]: value
    }));
  };

  const handleCreditFilterChange = (type, value) => {
    setCreditFilters(prev => ({
      ...prev,
      [type]: value
    }));
  };

  // Add new functions to handle filter button clicks
  const applyKarmaFilters = () => {
    setKarmaPage(1); // Reset to first page when applying filters
    fetchKarmaData();
  };

  const applyCreditFilters = () => {
    setCreditPage(1); // Reset to first page when applying filters
    fetchCreditData();
  };

  // Clear filter functions
  const clearKarmaFilters = () => {
    setKarmaFilters({
      startDate: '',
      endDate: '',
      search: '',
      minAmount: '',
      maxAmount: ''
    });
    setKarmaPage(1);
    fetchKarmaData();
  };

  const clearCreditFilters = () => {
    setCreditFilters({
      startDate: '',
      endDate: '',
      search: '',
      minAmount: '',
      maxAmount: ''
    });
    setCreditPage(1);
    fetchCreditData();
  };

  // Fetch Karma data
  const fetchKarmaData = async () => {
    if (!currentDao?._id || !chainId) return;
    
    setKarmaLoading(true);
    setKarmaError(null);
    try {
      const params = {
        daoId: currentDao._id,
        chainId,
        page: karmaPage,
        limit,
        startDate: karmaFilters.startDate || undefined,
        endDate: karmaFilters.endDate || undefined,
        email: karmaFilters.search || undefined
      };
      const data = await getListKarmaByDao(params)();
      setKarmaData(data?.data || []);
      setKarmaTotal(data?.pagination?.total || 0);
    } catch (err) {
      setKarmaError('Failed to fetch karma data');
      console.error('Karma fetch error:', err);
    } finally {
      setKarmaLoading(false);
    }
  };

  // Fetch Credit data
  const fetchCreditData = async () => {
    if (!currentDao?._id) return;
    
    setCreditLoading(true);
    setCreditError(null);
    try {
      const params = {
        daoId: currentDao._id,
        page: creditPage,
        limit,
        startDate: creditFilters.startDate || undefined,
        endDate: creditFilters.endDate || undefined,
        email: creditFilters.search || undefined
      };
      const data = await getListCreditByDao(params)();
      setCreditData(data?.data || []);
      setCreditTotal(data?.pagination?.total || 0);
    } catch (err) {
      setCreditError('Failed to fetch credit data');
      console.error('Credit fetch error:', err);
    } finally {
      setCreditLoading(false);
    }
  };

  // Filter Karma data
  const filteredKarmaData = karmaData;  // Remove client-side filtering since we're doing server-side filtering

  // Filter Credit data
  const filteredCreditData = creditData;  // Remove client-side filtering since we're doing server-side filtering

  // Initial data fetch
  useEffect(() => {
    const fetchInitialKarmaData = async () => {
      if (!currentDao?._id || !chainId) return;
      
      setKarmaLoading(true);
      setKarmaError(null);
      try {
        const params = {
          daoId: currentDao._id,
          chainId,
          page: karmaPage,
          limit
        };
        const data = await getListKarmaByDao(params)();
        setKarmaData(data?.data || []);
        setKarmaTotal(data?.pagination?.total || 0);
      } catch (err) {
        setKarmaError('Failed to fetch karma data');
        console.error('Karma fetch error:', err);
      } finally {
        setKarmaLoading(false);
      }
    };

    fetchInitialKarmaData();
  }, [currentDao?._id, chainId, karmaPage]); // Only re-fetch on page change or DAO/chain change

  useEffect(() => {
    const fetchInitialCreditData = async () => {
      if (!currentDao?._id) return;
      
      setCreditLoading(true);
      setCreditError(null);
      try {
        const params = {
          daoId: currentDao._id,
          page: creditPage,
          limit
        };
        const data = await getListCreditByDao(params)();
        setCreditData(data?.data || []);
        setCreditTotal(data?.pagination?.total || 0);
      } catch (err) {
        setCreditError('Failed to fetch credit data');
        console.error('Credit fetch error:', err);
      } finally {
        setCreditLoading(false);
      }
    };

    fetchInitialCreditData();
  }, [currentDao?._id, creditPage]); // Only re-fetch on page change or DAO change

  // Fetch Wallet History data
  useEffect(() => {
    const fetchWalletData = async () => {
      if (!currentDao?._id) return;
      
      setWalletLoading(true);
      setWalletError(null);
      try {
        const params = {
          daoId: currentDao._id
        };
        const response = await getHistoryWalletByDao(params)();
        const kWalletHistory = response?.data || null;
        const { katHistory, kyteHistory, creditHistory, karmaHistory } = kWalletHistory;
        let data = []
        if (katHistory){
          data = [...data, ...katHistory.map((v) => ({ ...v, unit: KAT }))];
        }
        if (kyteHistory){
          data = [...data, ...kyteHistory.map((v) => ({ ...v, unit: KYTE }))];
        }
        if (creditHistory){
          data = [...data, ...creditHistory.map((v) => ({ ...v, unit: CREDITS }))];
        }
        if (karmaHistory){
          data = [...data, ...karmaHistory.map((v) => ({ ...v, unit: KARMAS }))];
        }

        data.sort(function (a, b) {
          return new Date(b.reviewDate) - new Date(a.reviewDate);
        });

        setWalletData(data || []);
        setWalletTotal(data?.length || 0);
      } catch (err) {
        setWalletError('Failed to fetch wallet data');
        console.error('Wallet fetch error:', err);
      } finally {
        setWalletLoading(false);
      }
    };

    if (isOrganizer) {
      fetchWalletData();
    }

  }, [currentDao?._id]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return '#4CAF50';
      case 'rejected':
        return '#f44336';
      case 'pending':
        return '#ff9800';
      default:
        return '#fff';
    }
  };

  // Filter wallet data based on all filters
  const filteredWalletData = walletData.filter(item => {
    // Unit type filter
    if (!filters.units[item.unit]) return false;

    // Email filter
    if (filters.email && !item.userEmail?.toLowerCase().includes(filters.email.toLowerCase())) return false;

    // Status filter
    if (filters.status !== 'all' && item.status?.toLowerCase() !== filters.status.toLowerCase()) return false;

    // Date range filter
    if (filters.startDate) {
      const itemDate = new Date(item.reviewDate || item.submittedDate);
      const startDate = new Date(filters.startDate);
      if (itemDate < startDate) return false;
    }

    if (filters.endDate) {
      const itemDate = new Date(item.reviewDate || item.submittedDate);
      const endDate = new Date(filters.endDate);
      endDate.setHours(23, 59, 59); // Include the entire end date
      if (itemDate > endDate) return false;
    }

    return true;
  });

  // Calculate paginated wallet data from filtered data
  const startIndex = (walletPage - 1) * limit;
  const endIndex = startIndex + limit;
  const paginatedWalletData = filteredWalletData.slice(startIndex, endIndex);
  
  // Handle filter changes
  const handleFilterChange = (type, value) => {
    if (type === 'unit') {
      setFilters(prev => ({
        ...prev,
        units: {
          ...prev.units,
          [value]: !prev.units[value]
        }
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        [type]: value
      }));
    }
    setWalletPage(1); // Reset to first page when filter changes
  };

  // Add function to handle view click
  const handleViewClick = async (email, type) => {
    setDetailLoading(true);
    setModalType(type);
    try {
      const data = await getKarmaCreditActivityByEmail({ 
        daoId: currentDao._id,
        email: email 
      })();
      console.log('data', data)
      let activities = []
      if (type === 'karma') {
        activities = data?.data?.karmas
      } else {
        activities = data?.data?.credits
      }
      // Transform the data based on type
      const transformedData = {
        total: type === 'karma' ? data?.data?.totalKarma : data?.data?.totalCredit,
        activities: activities.map(activity => ({
          activity: activity.note,
          date: activity.date,
          amount: activity.amount
        })) || []
      };
      
      setModalData(transformedData);
      setModalOpen(true);
    } catch (error) {
      console.error(`Error fetching ${type} details:`, error);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <Box sx={{ p: 4 }}>
      {/* Horizontal Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'rgba(255, 255, 255, 0.1)', mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="engagement tabs"
          sx={{
            '& .MuiTabs-indicator': {
              backgroundColor: '#00EDC3',
            },
            '& .MuiTab-root': {
              color: 'rgba(255, 255, 255, 0.7)',
              '&.Mui-selected': {
                color: '#00EDC3',
              },
            },
          }}
        >
          <Tab label="Karma and Credit" />
          {isOrganizer && (<Tab label="Kambria Wallet" />)}
        </Tabs>
      </Box>

      {/* Karma and Credit Tab Content */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          {/* Karma Data Table */}
          <Grid item xs={12}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: '#03edc3' }}>Karma Activity List</h3>
            </div>

            {/* Karma Filters */}
            <Box sx={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.05)', 
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '20px'
            }}>
              <Grid container spacing={2}>
                {/* Date Range Filters */}
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    type="date"
                    label="Start Date"
                    value={karmaFilters.startDate}
                    onChange={(e) => handleKarmaFilterChange('startDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: '#fff',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.23)',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255, 255, 255, 0.7)',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    type="date"
                    label="End Date"
                    value={karmaFilters.endDate}
                    onChange={(e) => handleKarmaFilterChange('endDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: '#fff',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.23)',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255, 255, 255, 0.7)',
                      },
                    }}
                  />
                </Grid>

                {/* Search Filter */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Search Username/Email"
                    value={karmaFilters.search}
                    onChange={(e) => handleKarmaFilterChange('search', e.target.value)}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: '#fff',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.23)',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255, 255, 255, 0.7)',
                      },
                    }}
                  />
                </Grid>

                {/* Filter Buttons */}
                <Grid item xs={12} sm={6} md={2} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      onClick={applyKarmaFilters}
                      sx={{
                        backgroundColor: '#00ffe0',
                        color: '#181c22',
                        '&:hover': {
                          backgroundColor: '#00ccb4'
                        }
                      }}
                    >
                      Filter
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={clearKarmaFilters}
                      sx={{
                        borderColor: '#00ffe0',
                        color: '#00ffe0',
                        '&:hover': {
                          borderColor: '#00ccb4',
                          color: '#00ccb4'
                        }
                      }}
                    >
                      Clear
                    </Button>
                  </Box>
                </Grid>
              </Grid>

              {/* Results count */}
              <Box sx={{ mt: 2, color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px' }}>
                Showing {filteredKarmaData.length} of {karmaData.length} karma records
              </Box>
            </Box>

            {karmaLoading && <div style={{ color: '#fff' }}>Loading karma data...</div>}
            {karmaError && <div style={{ color: 'red' }}>{karmaError}</div>}
            
            {!karmaLoading && !karmaError && (
              <>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, marginTop: '20px', background: '#23272f', color: '#00ffe0', borderRadius: 8, overflow: 'hidden', boxShadow: '0 2px 8px #0006' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#181c22' }}>
                        <th style={{ padding: '14px', textAlign: 'left', borderBottom: '2px solid #00ffe0', color: '#00ffe0', fontWeight: 600 }}>Username</th>
                        <th style={{ padding: '14px', textAlign: 'left', borderBottom: '2px solid #00ffe0', color: '#00ffe0', fontWeight: 600 }}>Email</th>
                        <th style={{ padding: '14px', textAlign: 'left', borderBottom: '2px solid #00ffe0', color: '#00ffe0', fontWeight: 600 }}>Karma</th>
                        <th style={{ padding: '14px', textAlign: 'left', borderBottom: '2px solid #00ffe0', color: '#00ffe0', fontWeight: 600 }}>Detail Link</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredKarmaData.length > 0 ? filteredKarmaData.map((karma, idx) => (
                        <tr key={karma._id || idx}
                          style={{ borderBottom: '1px solid #00ffe0', background: idx % 2 === 0 ? '#23272f' : '#181c22', transition: 'background 0.2s' }}
                          onMouseOver={e => e.currentTarget.style.background = '#26313a'}
                          onMouseOut={e => e.currentTarget.style.background = idx % 2 === 0 ? '#23272f' : '#181c22'}
                        >
                          <td style={{ padding: '12px', color: '#fff' }}>{karma.username || 'Unknown'}</td>
                          <td style={{ padding: '12px', color: '#fff' }}>{karma._id || 'Unknown'}</td>
                          <td style={{ padding: '12px', color: '#00ffe0', fontWeight: 600 }}>{karma.total || 0}</td>
                          <td style={{ padding: '12px', color: '#fff' }}>
                            <Button
                              onClick={() => handleViewClick(karma._id, 'karma')}
                              disabled={detailLoading}
                              sx={{
                                color: '#00ffe0',
                                '&:hover': {
                                  color: '#00ccb4',
                                  backgroundColor: 'rgba(0, 255, 224, 0.08)'
                                }
                              }}
                            >
                              View
                            </Button>
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="4" style={{ padding: '20px', textAlign: 'center', color: '#fff' }}>
                            No karma data available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                
                {/* Karma Pagination */}
                <div style={{ marginTop: '20px' }}>
                  <button 
                    onClick={() => setKarmaPage(p => Math.max(1, p - 1))}
                    disabled={karmaPage === 1}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#00ffe0',
                      color: '#181c22',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      marginRight: '10px',
                      opacity: karmaPage === 1 ? 0.5 : 1
                    }}
                  >
                    Previous Page
                  </button>
                  <span style={{ margin: '0 10px', color: '#fff' }}>Page {karmaPage}</span>
                  <button 
                    onClick={() => setKarmaPage(p => p + 1)}
                    disabled={karmaPage >= Math.ceil(karmaTotal / limit)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#00ffe0',
                      color: '#181c22',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      opacity: karmaPage >= Math.ceil(karmaTotal / limit) ? 0.5 : 1
                    }}
                  >
                    Next Page
                  </button>
                </div>
              </>
            )}
          </Grid>

          {/* Credit Data Table */}
          <Grid item xs={12}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: '#03edc3' }}>Credit Activity List</h3>
            </div>

            {/* Credit Filters */}
            <Box sx={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.05)', 
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '20px'
            }}>
              <Grid container spacing={2}>
                {/* Date Range Filters */}
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    type="date"
                    label="Start Date"
                    value={creditFilters.startDate}
                    onChange={(e) => handleCreditFilterChange('startDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: '#fff',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.23)',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255, 255, 255, 0.7)',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    type="date"
                    label="End Date"
                    value={creditFilters.endDate}
                    onChange={(e) => handleCreditFilterChange('endDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: '#fff',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.23)',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255, 255, 255, 0.7)',
                      },
                    }}
                  />
                </Grid>

                {/* Search Filter */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    label="Search Username/Email"
                    value={creditFilters.search}
                    onChange={(e) => handleCreditFilterChange('search', e.target.value)}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: '#fff',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.23)',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255, 255, 255, 0.7)',
                      },
                    }}
                  />
                </Grid>

                {/* Filter Buttons */}
                <Grid item xs={12} sm={6} md={2} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      onClick={applyCreditFilters}
                      sx={{
                        backgroundColor: '#00ffe0',
                        color: '#181c22',
                        '&:hover': {
                          backgroundColor: '#00ccb4'
                        }
                      }}
                    >
                      Filter
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={clearCreditFilters}
                      sx={{
                        borderColor: '#00ffe0',
                        color: '#00ffe0',
                        '&:hover': {
                          borderColor: '#00ccb4',
                          color: '#00ccb4'
                        }
                      }}
                    >
                      Clear
                    </Button>
                  </Box>
                </Grid>
              </Grid>

              {/* Results count */}
              <Box sx={{ mt: 2, color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px' }}>
                Showing {filteredCreditData.length} of {creditData.length} credit records
              </Box>
            </Box>

            {creditLoading && <div style={{ color: '#fff' }}>Loading credit data...</div>}
            {creditError && <div style={{ color: 'red' }}>{creditError}</div>}
            
            {!creditLoading && !creditError && (
              <>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, marginTop: '20px', background: '#23272f', color: '#00ffe0', borderRadius: 8, overflow: 'hidden', boxShadow: '0 2px 8px #0006' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#181c22' }}>
                        <th style={{ padding: '14px', textAlign: 'left', borderBottom: '2px solid #00ffe0', color: '#00ffe0', fontWeight: 600 }}>Username</th>
                        <th style={{ padding: '14px', textAlign: 'left', borderBottom: '2px solid #00ffe0', color: '#00ffe0', fontWeight: 600 }}>Email</th>
                        <th style={{ padding: '14px', textAlign: 'left', borderBottom: '2px solid #00ffe0', color: '#00ffe0', fontWeight: 600 }}>Credit</th>
                        <th style={{ padding: '14px', textAlign: 'left', borderBottom: '2px solid #00ffe0', color: '#00ffe0', fontWeight: 600 }}>Detail Link</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCreditData.length > 0 ? filteredCreditData.map((credit, idx) => (
                        <tr key={credit._id || idx}
                          style={{ borderBottom: '1px solid #00ffe0', background: idx % 2 === 0 ? '#23272f' : '#181c22', transition: 'background 0.2s' }}
                          onMouseOver={e => e.currentTarget.style.background = '#26313a'}
                          onMouseOut={e => e.currentTarget.style.background = idx % 2 === 0 ? '#23272f' : '#181c22'}
                        >
                          <td style={{ padding: '12px', color: '#fff' }}>{credit.username || 'Unknown'}</td>
                          <td style={{ padding: '12px', color: '#fff' }}>{credit._id || 'Unknown'}</td>
                          <td style={{ padding: '12px', color: '#00ffe0', fontWeight: 600 }}>{credit.total || 0}</td>
                          <td style={{ padding: '12px', color: '#fff' }}>
                            <Button
                              onClick={() => handleViewClick(credit._id, 'credit')}
                              disabled={detailLoading}
                              sx={{
                                color: '#00ffe0',
                                '&:hover': {
                                  color: '#00ccb4',
                                  backgroundColor: 'rgba(0, 255, 224, 0.08)'
                                }
                              }}
                            >
                              View
                            </Button>
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="4" style={{ padding: '20px', textAlign: 'center', color: '#fff' }}>
                            No credit data available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                
                {/* Credit Pagination */}
                <div style={{ marginTop: '20px' }}>
                  <button 
                    onClick={() => setCreditPage(p => Math.max(1, p - 1))}
                    disabled={creditPage === 1}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#00ffe0',
                      color: '#181c22',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      marginRight: '10px',
                      opacity: creditPage === 1 ? 0.5 : 1
                    }}
                  >
                    Previous Page
                  </button>
                  <span style={{ margin: '0 10px', color: '#fff' }}>Page {creditPage}</span>
                  <button 
                    onClick={() => setCreditPage(p => p + 1)}
                    disabled={creditPage >= Math.ceil(creditTotal / limit)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#00ffe0',
                      color: '#181c22',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      opacity: creditPage >= Math.ceil(creditTotal / limit) ? 0.5 : 1
                    }}
                  >
                    Next Page
                  </button>
                </div>
              </>
            )}
          </Grid>
        </Grid>
      </TabPanel>

      {/* Kambria Wallet Tab Content */}
      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: '#03edc3' }}>Wallet Transaction History</h3>
            </div>

            {/* Enhanced Filter Section */}
            <Box sx={{ 
              backgroundColor: 'rgba(255, 255, 255, 0.05)', 
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '20px'
            }}>
              <Grid container spacing={2}>
                {/* Date Range Filters */}
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    type="date"
                    label="Start Date"
                    value={filters.startDate}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: '#fff',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.23)',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255, 255, 255, 0.7)',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    type="date"
                    label="End Date"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: '#fff',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.23)',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255, 255, 255, 0.7)',
                      },
                    }}
                  />
                </Grid>

                {/* Email Filter */}
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Search Email"
                    value={filters.email}
                    onChange={(e) => handleFilterChange('email', e.target.value)}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        color: '#fff',
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.23)',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: 'rgba(255, 255, 255, 0.7)',
                      },
                    }}
                  />
                </Grid>

                {/* Status Filter */}
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Status</InputLabel>
                    <Select
                      value={filters.status}
                      onChange={(e) => handleFilterChange('status', e.target.value)}
                      sx={{
                        color: '#fff',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.23)',
                        },
                      }}
                    >
                      <MenuItem value="all">All Status</MenuItem>
                      <MenuItem value="approved">Approved</MenuItem>
                      <MenuItem value="rejected">Rejected</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Unit Type Filters */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                    {Object.keys(filters.units).map(unit => (
                      <label key={unit} style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px', 
                        color: '#fff', 
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}>
                        <input
                          type="checkbox"
                          checked={filters.units[unit]}
                          onChange={() => handleFilterChange('unit', unit)}
                          style={{
                            width: '16px',
                            height: '16px',
                            accentColor: '#00EDC3',
                            cursor: 'pointer'
                          }}
                        />
                        {unit}
                      </label>
                    ))}
                    <div style={{ marginLeft: 'auto', color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px' }}>
                      Showing {filteredWalletData.length} of {walletData.length} transactions
                    </div>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {walletLoading && <div style={{ color: '#fff' }}>Loading wallet data...</div>}
            {walletError && <div style={{ color: 'red' }}>{walletError}</div>}
            
            {!walletLoading && !walletError && (
              <>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, marginTop: '20px', background: '#23272f', color: '#00ffe0', borderRadius: 8, overflow: 'hidden', boxShadow: '0 2px 8px #0006' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#181c22' }}>
                        <th style={{ padding: '14px', textAlign: 'left', borderBottom: '2px solid #00ffe0', color: '#00ffe0', fontWeight: 600 }}>Users</th>
                        <th style={{ padding: '14px', textAlign: 'left', borderBottom: '2px solid #00ffe0', color: '#00ffe0', fontWeight: 600 }}>Amount</th>
                        <th style={{ padding: '14px', textAlign: 'left', borderBottom: '2px solid #00ffe0', color: '#00ffe0', fontWeight: 600 }}>Activity</th>
                        <th style={{ padding: '14px', textAlign: 'left', borderBottom: '2px solid #00ffe0', color: '#00ffe0', fontWeight: 600 }}>Description</th>
                        <th style={{ padding: '14px', textAlign: 'left', borderBottom: '2px solid #00ffe0', color: '#00ffe0', fontWeight: 600 }}>Submitted By</th>
                        <th style={{ padding: '14px', textAlign: 'left', borderBottom: '2px solid #00ffe0', color: '#00ffe0', fontWeight: 600 }}>Submitted Date</th>
                        <th style={{ padding: '14px', textAlign: 'left', borderBottom: '2px solid #00ffe0', color: '#00ffe0', fontWeight: 600 }}>Review By</th>
                        <th style={{ padding: '14px', textAlign: 'left', borderBottom: '2px solid #00ffe0', color: '#00ffe0', fontWeight: 600 }}>Review Date</th>
                        <th style={{ padding: '14px', textAlign: 'left', borderBottom: '2px solid #00ffe0', color: '#00ffe0', fontWeight: 600 }}>Type</th>
                        <th style={{ padding: '14px', textAlign: 'left', borderBottom: '2px solid #00ffe0', color: '#00ffe0', fontWeight: 600 }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedWalletData.length > 0 ? paginatedWalletData.map((wallet, idx) => (
                        <tr key={wallet._id || idx}
                          style={{ borderBottom: '1px solid #00ffe0', background: idx % 2 === 0 ? '#23272f' : '#181c22', transition: 'background 0.2s' }}
                          onMouseOver={e => e.currentTarget.style.background = '#26313a'}
                          onMouseOut={e => e.currentTarget.style.background = idx % 2 === 0 ? '#23272f' : '#181c22'}
                        >
                          <td style={{ padding: '12px', color: '#fff' }}>{wallet.userEmail || wallet.email || 'Unknown'}</td>
                          <td style={{ padding: '12px', color: '#00ffe0', fontWeight: 600 }}>{wallet.amount || 0}</td>
                          <td style={{ padding: '12px', color: '#fff' }}>{wallet.activity || 'N/A'}</td>
                          <td style={{ padding: '12px', color: '#fff', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {wallet.description || wallet.note || 'No description'}
                          </td>
                          <td style={{ padding: '12px', color: '#fff' }}>{wallet.submittedBy || wallet.createdBy || 'N/A'}</td>
                          <td style={{ padding: '12px', color: '#fff' }}>
                            {wallet.submittedDate ? new Date(wallet.submittedDate).toLocaleDateString() : 
                             wallet.createdAt ? new Date(wallet.createdAt).toLocaleDateString() : 'N/A'}
                          </td>
                          <td style={{ padding: '12px', color: '#fff' }}>{wallet.reviewBy || wallet.reviewedBy || 'N/A'}</td>
                          <td style={{ padding: '12px', color: '#fff' }}>
                            {wallet.reviewDate ? new Date(wallet.reviewDate).toLocaleDateString() : 
                             wallet.updatedAt ? new Date(wallet.updatedAt).toLocaleDateString() : 'N/A'}
                          </td>
                          <td style={{ padding: '12px', color: '#fff' }}>{wallet.unit || 'KAT'}</td>
                          <td style={{ padding: '12px' }}>
                            <span style={{ 
                              color: getStatusColor(wallet.status),
                              fontWeight: 600,
                              padding: '4px 8px',
                              borderRadius: '12px',
                              border: `1px solid ${getStatusColor(wallet.status)}`,
                              fontSize: '12px',
                              textTransform: 'uppercase'
                            }}>
                              {wallet.status || 'PENDING'}
                            </span>
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="10" style={{ padding: '20px', textAlign: 'center', color: '#fff' }}>
                            No wallet transaction data available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                
                {/* Wallet Pagination */}
                <div style={{ marginTop: '20px' }}>
                  <button 
                    onClick={() => setWalletPage(p => Math.max(1, p - 1))}
                    disabled={walletPage === 1}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#00ffe0',
                      color: '#181c22',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      marginRight: '10px',
                      opacity: walletPage === 1 ? 0.5 : 1
                    }}
                  >
                    Previous Page
                  </button>
                                     <span style={{ margin: '0 10px', color: '#fff' }}>
                     Page {walletPage} of {Math.ceil(filteredWalletData.length / limit)} ({filteredWalletData.length} filtered transactions)
                   </span>
                                     <button 
                     onClick={() => setWalletPage(p => p + 1)}
                     disabled={walletPage >= Math.ceil(filteredWalletData.length / limit)}
                     style={{
                       padding: '8px 16px',
                       backgroundColor: '#00ffe0',
                       color: '#181c22',
                       border: 'none',
                       borderRadius: '4px',
                       cursor: 'pointer',
                       fontWeight: 'bold',
                       opacity: walletPage >= Math.ceil(filteredWalletData.length / limit) ? 0.5 : 1
                     }}
                   >
                     Next Page
                   </button>
                </div>
              </>
            )}
          </Grid>
        </Grid>
      </TabPanel>

      {/* Add the modal component */}
      <DetailModal 
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        data={modalData}
        type={modalType}
      />
    </Box>
  );
};

export default connect(null, {})(EngagementTab);
